export const UGC_BATCH_SIZE = 8;

export function nextVisibleCount(current, total, batchSize = UGC_BATCH_SIZE) {
  return Math.min(total, Math.max(0, current) + batchSize);
}

export function getRailScrollAmount(cardWidth, gap, direction) {
  const distance = Math.max(0, cardWidth) + Math.max(0, gap);
  return direction < 0 ? -distance : distance;
}

export function interleaveByKey(items, getKey = (item) => item?.dataset?.productKey || '') {
  const groups = new Map();

  items.forEach((item, index) => {
    const rawKey = getKey(item, index);
    const key = rawKey || `__item-${index}`;
    const group = groups.get(key) ?? { items: [], firstIndex: index };
    group.items.push(item);
    groups.set(key, group);
  });

  const ordered = [];
  let lastGroup = null;

  while (ordered.length < items.length) {
    const candidates = [...groups.entries()]
      .filter(([, group]) => group.items.length > 0 && group !== lastGroup)
      .sort(([, left], [, right]) => {
        const sizeDifference = right.items.length - left.items.length;
        return sizeDifference || left.firstIndex - right.firstIndex;
      });

    const fallback = candidates.length > 0
      ? candidates
      : [...groups.entries()]
        .filter(([, group]) => group.items.length > 0)
        .sort(([, left], [, right]) => left.firstIndex - right.firstIndex);
    const [, group] = fallback[0];

    ordered.push(group.items.shift());
    lastGroup = group;
  }

  return ordered;
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};
let activeController = null;

export class GvodeUgc extends HTMLElementBase {
  constructor() {
    super();
    this.dialog = null;
    this.video = null;
    this.dialogTitle = null;
    this.error = null;
    this.focusReturnTarget = null;
    this.controllerConnected = false;

    this.handleClick = this.handleClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleMediaError = this.handleMediaError.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.controllerConnected) return;

    this.controllerConnected = true;
    this.cachePlayerElements();
    this.reorderProductRail();
    this.addEventListener('click', this.handleClick);
    this.dialog?.addEventListener('cancel', this.handleCancel);
    this.video?.addEventListener('error', this.handleMediaError);
    globalThis.document?.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  disconnectedCallback() {
    if (!this.controllerConnected) return;

    this.removeEventListener('click', this.handleClick);
    this.dialog?.removeEventListener('cancel', this.handleCancel);
    this.video?.removeEventListener('error', this.handleMediaError);
    globalThis.document?.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.closePlayer({ restoreFocus: false });
    this.controllerConnected = false;
  }

  cachePlayerElements() {
    this.dialog = this.querySelector('[data-ugc-dialog]');
    this.video = this.querySelector('[data-ugc-video]');
    this.dialogTitle = this.querySelector('[data-ugc-dialog-title]');
    this.error = this.querySelector('[data-ugc-error]');
  }

  handleClick(event) {
    const closest = (selector) => event.target?.closest?.(selector);
    const playButton = closest('[data-ugc-play]');
    if (playButton) {
      this.openPlayer(playButton);
      return;
    }

    if (closest('[data-ugc-close]')) {
      this.closePlayer();
      return;
    }

    if (closest('[data-ugc-prev]')) {
      this.scrollRail(-1);
      return;
    }

    if (closest('[data-ugc-next]')) {
      this.scrollRail(1);
      return;
    }

    const showMoreButton = closest('[data-ugc-show-more]');
    if (showMoreButton) {
      this.showMore(showMoreButton);
      return;
    }

    const reviewToggle = closest('[data-review-toggle]');
    if (reviewToggle) {
      this.toggleReview(reviewToggle);
      return;
    }

    if (event.target === this.dialog) {
      this.closePlayer();
    }
  }

  openPlayer(playButton) {
    const source = playButton.dataset.ugcVideoSrc;
    if (!source) return;

    this.cachePlayerElements();
    if (!this.dialog || !this.video) return;

    if (activeController) {
      activeController.closePlayer({ restoreFocus: activeController !== this });
    }

    this.focusReturnTarget = playButton;
    this.clearError();
    this.dialogTitle.textContent = playButton.dataset.ugcVideoTitle || 'UGC video';

    const poster = playButton.dataset.ugcVideoPoster;
    if (poster) {
      this.video.poster = poster;
    } else {
      this.video.removeAttribute('poster');
    }

    this.video.src = source;
    this.video.load();
    activeController = this;

    try {
      this.dialog.showModal();
    } catch {
      this.closePlayer();
      return;
    }

    try {
      const playAttempt = this.video.play();
      playAttempt?.catch(() => {});
    } catch {
      // Native controls remain available when a browser rejects programmatic playback.
    }
  }

  handleCancel(event) {
    event.preventDefault();
    this.closePlayer();
  }

  handleVisibilityChange() {
    if (globalThis.document?.hidden && activeController === this) {
      this.video?.pause();
    }
  }

  handleMediaError() {
    if (!this.video?.hasAttribute('src')) return;

    if (this.error) this.error.hidden = false;
    this.teardownVideoSource();
  }

  teardownVideoSource() {
    if (!this.video) return;

    this.video.pause();
    try {
      this.video.currentTime = 0;
    } catch {
      // A failed or not-yet-ready media resource may not be seekable.
    }
    this.video.removeAttribute('src');
    this.video.load();
  }

  clearError() {
    if (this.error) this.error.hidden = true;
  }

  closePlayer({ restoreFocus = true } = {}) {
    this.teardownVideoSource();
    this.clearError();

    if (this.dialog?.open) this.dialog.close();
    if (activeController === this) activeController = null;

    if (restoreFocus && this.focusReturnTarget?.isConnected) {
      this.focusReturnTarget.focus({ preventScroll: true });
    }
    this.focusReturnTarget = null;
  }

  showMore(button) {
    const items = [...this.querySelectorAll('[data-ugc-item]')];
    const declaredTotal = Number.parseInt(button.dataset.ugcTotal, 10);
    const total = Number.isFinite(declaredTotal)
      ? Math.min(Math.max(0, declaredTotal), items.length)
      : items.length;
    const declaredVisible = Number.parseInt(this.dataset.ugcVisibleCount, 10);
    const current = Number.isFinite(declaredVisible)
      ? declaredVisible
      : items.filter((item) => !item.hidden).length;
    const next = nextVisibleCount(current, total);

    for (let index = Math.max(0, current); index < next; index += 1) {
      items[index].hidden = false;
    }

    this.dataset.ugcVisibleCount = String(next);
    const reachedEnd = next >= total;
    const firstRevealedIndex = Math.max(0, current);
    if (reachedEnd && firstRevealedIndex < next) {
      items[firstRevealedIndex]
        ?.querySelector('[data-ugc-play]')
        ?.focus({ preventScroll: true });
    }
    button.hidden = reachedEnd;
  }

  toggleReview(button) {
    const card = button.closest('[data-review-card]');
    const body = card?.querySelector('[data-review-body]');
    if (!card || !body) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    body.classList.toggle('is-expanded', !expanded);

    const label = button.querySelector('[data-review-toggle-label]');
    if (label) label.textContent = expanded ? 'Read more' : 'Show less';
  }

  reorderProductRail() {
    if (!this.classList.contains('gvode-reviews__ugc-collection')) return;

    const rail = this.querySelector('[data-ugc-rail]');
    if (!rail) return;

    const items = [...rail.children].filter((item) => item.matches?.('[data-ugc-item]'));
    if (items.length < 2) return;

    const ordered = interleaveByKey(items);
    if (ordered.every((item, index) => item === items[index])) return;

    const fragment = globalThis.document?.createDocumentFragment?.();
    if (!fragment) return;

    ordered.forEach((item) => fragment.append(item));
    rail.append(fragment);
  }

  scrollRail(direction) {
    const rail = this.querySelector('[data-ugc-rail]');
    const card = rail?.querySelector('[data-ugc-item]:not([hidden])')
      ?? rail?.querySelector('.gvode-ugc-card:not([hidden])');
    if (!rail || !card || typeof rail.scrollBy !== 'function') return;

    const cardWidth = card.getBoundingClientRect().width;
    const styles = globalThis.getComputedStyle?.(rail);
    const gap = Number.parseFloat(styles?.columnGap || styles?.gap || '') || 0;
    rail.scrollBy({
      left: getRailScrollAmount(cardWidth, gap, direction)
    });
  }
}

if (globalThis.customElements && !customElements.get('gvode-ugc')) {
  customElements.define('gvode-ugc', GvodeUgc);
}
